import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: '首頁' },
    { path: '/mentors', label: '找導師' },
    { path: '/courses', label: '課程' },
    { path: '/blog', label: '資源中心' },
    { path: '/contact', label: '聯絡我們' },
  ];

  const isLight = scrolled || location.pathname !== '/';

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isLight ? 'bg-white border-b border-slate-100' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
          <img
            src="/assets/readdy/static-358c7e6691b9b58b5179c58a2715558b.jpg"
            alt="NexusGo"
            className="w-8 h-8 rounded-lg object-cover"
          />
          <span className={`font-bold text-lg tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`}>
            NexusGo
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-0.5">
          {navLinks.map((link) => {
            const active = isActive(link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-2.5 py-2 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap rounded-md ${
                  active
                    ? isLight ? 'text-violet-600' : 'text-white'
                    : isLight ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-50' : 'text-white/70 hover:text-white hover:bg-white/10'
                } ${active && isLight ? 'bg-violet-50' : ''} ${active && !isLight ? 'bg-white/15' : ''}`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0.5 left-2.5 right-2.5 h-0.5 rounded-full transition-all duration-300 ${
                    active
                      ? isLight ? 'bg-violet-500 opacity-100 scale-x-100' : 'bg-white opacity-80 scale-x-100'
                      : 'opacity-0 scale-x-0'
                  }`}
                  style={{ transformOrigin: 'left' }}
                />
              </Link>
            );
          })}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <>
              <Link
                to="/account"
                className={`text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                  isLight ? 'text-slate-500 hover:text-slate-900' : 'text-white/80 hover:text-white'
                }`}
              >
                {user?.nickname || user?.email || '我的帳戶'}
              </Link>
              <button
                onClick={logout}
                className={`px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
                  isLight
                    ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    : 'bg-white/15 text-white border border-white/25 hover:bg-white/25 backdrop-blur-sm'
                }`}
              >
                退出登入
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className={`text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                  isLight ? 'text-slate-500 hover:text-slate-900' : 'text-white/80 hover:text-white'
                }`}
              >
                登入
              </Link>
              <Link
                to="/register"
                className={`px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
                  isLight
                    ? 'bg-violet-600 text-white hover:bg-violet-700'
                    : 'bg-white/15 text-white border border-white/25 hover:bg-white/25 backdrop-blur-sm'
                }`}
              >
                立即加入
              </Link>
            </>
          )}
        </div>

        <button
          className={`md:hidden cursor-pointer ${isLight ? 'text-slate-800' : 'text-white'}`}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-b border-slate-100 px-4 py-3 flex flex-col gap-1">
          {navLinks.map((link) => {
            const active = isActive(link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 ${
                  active ? 'text-violet-600 bg-violet-50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                {active && <span className="w-1 h-4 bg-violet-500 rounded-full flex-shrink-0" />}
                {!active && <span className="w-1 h-4 flex-shrink-0" />}
                {link.label}
              </Link>
            );
          })}
          <div className="pt-2 mt-1 border-t border-slate-100">
            {isAuthenticated ? (
              <div className="flex flex-col gap-2">
                <Link
                  to="/account"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-2.5 bg-violet-600 text-white rounded-lg text-sm font-semibold text-center cursor-pointer hover:bg-violet-700 transition-colors"
                >
                  我的帳戶
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setMobileOpen(false);
                  }}
                  className="block px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold text-center cursor-pointer hover:bg-slate-200 transition-colors"
                >
                  退出登入
                </button>
              </div>
            ) : (
              <Link
                to="/register"
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-2.5 bg-violet-600 text-white rounded-lg text-sm font-semibold text-center cursor-pointer hover:bg-violet-700 transition-colors"
              >
                立即加入
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
